import * as React from 'react'
import rehypeParse from 'rehype-parse'
import rehypeRemark from 'rehype-remark'
import remarkGfm from 'remark-gfm'
import remarkStringify from 'remark-stringify'
import {
  Block,
  CommonBlocksCompoundIndexes,
  ComponentBlock,
  DateBlock,
  InstanceBlock,
  ListBlock,
  MediaBlock,
  RichTextBlock,
  VariantData,
  getTitleBasedOnSelectedVariants,
  getValueBasedOnSelectedVariants,
  getVariantsKey,
  isSelectBlock,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import { GridCell, GridCellKind, Item } from '@glideapps/glide-data-grid'
import { useTabsStore } from '../../../../tabs/store'
import { useSelectedView } from '../use-selected-view'
import { TreeManager, TreeNode } from '@basehub/utils/tree/index'
import {
  enhanceGeneratedHTML,
  richTextExtensionsForTransformations,
  stateToJson,
  tipTapJSONToHTMLString,
} from '@basehub/tiptap-api-utils'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { SymbolIcon, TrashIcon, UploadIcon } from '@radix-ui/react-icons'
import { Action } from './custom-cells/utils'
import { unified } from 'unified'
import { getBlockFromColumnId } from '@basehub/data/replicache/schema/block/list/utils'
import {
  getDateBlockPlaceholder,
  isoStringToDisplayDate,
} from '~/lib/date-utils'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { resolveVisibleColumns } from './columns'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { EditorMode } from '../../../../editor-mode-provider'
import { TableProps } from './components'
import { rIC } from '~/lib/request-idle-callback'
import { LIST_BLOCK_DARK_THEME } from './theme'
import { useGetEventData } from '~/hooks-data/use-get-event-data'
import { getDefaultMimeTypesByCategory } from '@basehub/data/replicache/schema/block/valid-mime-types'
// import { usePossibleReferences } from '../../../reference/use-possible-references'

const DEFAULT_EMPTY_CELL: GridCell = {
  kind: GridCellKind.Text,
  data: '',
  allowOverlay: false,
  displayData: '',
  readonly: true,
}

const hashMap = new Map<string, string>()

export const useGetCellContent = ({
  rows,
  block,
  treeManagerRef,
  template,
  editorMode,
  tabPrefix,
}: {
  rows: (ComponentBlock | InstanceBlock)[]
  block: ListBlock
  treeManagerRef: React.RefObject<null | TreeManager<
    Block,
    CommonBlocksCompoundIndexes
  >>
  template?: ComponentBlock
  editorMode?: EditorMode
  tabPrefix: TableProps['tabPrefix']
}) => {
  // const { getPossibleReferences } = usePossibleReferences()
  const addTab = useTabsStore((s) => s.addTab)
  const [selectedView] = useSelectedView(block)
  const replicache = useRepoReplicache()?.data?.replicache
  const readonly = editorMode === 'read'
  const rtIdleCallback = React.useRef<number | null>(null)
  const [rerender, setRerender] = React.useState(0)
  const { highestEventValue, getEventData } = useGetEventData({
    onFetched: () => setRerender((prev) => prev + 1),
  })
  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: block,
  })

  const getRichTextContent = React.useCallback(
    (block: RichTextBlock, variants: VariantData[]) => {
      let key: string
      if (variants.length) {
        const overrideKey = getVariantsKey(variants)
        key = `${block.id}-${block.updatedAt}-${overrideKey}`
      } else {
        key = block.id + ':' + block.updatedAt
      }

      if (hashMap.has(key)) {
        return hashMap.get(key)!
      }

      if (!rtIdleCallback.current) {
        rtIdleCallback.current = rIC(async () => {
          const value = getValueBasedOnSelectedVariants({
            block,
            variantsToFilterBy: variants,
          })

          const rawHTML = tipTapJSONToHTMLString({
            isEmpty: !!value.state,
            json: stateToJson(value.state),
            extensions: richTextExtensionsForTransformations,
          })

          const enhancedHtml = await enhanceGeneratedHTML({
            html: rawHTML,
            options: { slugs: true, toc: undefined },
          })
          const file = await unified()
            .use(rehypeParse)
            .use(rehypeRemark)
            .use(remarkGfm)
            .use(remarkStringify)
            .process(enhancedHtml ?? '')
          hashMap.set(key, String(file).slice(0, 80))
          rtIdleCallback.current = null
          setRerender((prev) => prev + 1)
        })
      }

      return 'Loading...'
    },
    []
  )

  const getAudioContent = React.useCallback((block: MediaBlock) => {
    if (block.value.status !== 'uploaded' || block.value.type !== 'audio') {
      return null
    }
    if (hashMap.has(block.id + ':' + block.updatedAt)) {
      return hashMap.get(block.id + ':' + block.updatedAt)!
    }

    if (block.value.status === 'uploaded') {
      const audio = new Audio(block.value.url)
      audio.onloadeddata = () => {
        if (!audio.duration) return
        const duration = Math.round(audio.duration)
        const minutes = Math.floor(duration / 60)
        const remainingSeconds = duration % 60
        hashMap.set(
          block.id + ':' + block.updatedAt,
          `${minutes}:${remainingSeconds}`
        )
      }
    }

    return null
  }, [])

  const onRemoveAssetBlock = React.useCallback(
    (cell: MediaBlock, onFinishedEditing: () => void, path: string | null) => {
      if (!replicache || !path) return

      replicache.mutate
        .updateBlock({
          id: cell.id,
          isoNow: new Date().toISOString(),
          path,
          strategy: 'merge',
          instanceChildTarget: cell.instanceChildTarget,
          data: {
            type: cell.type,
            value: { status: 'idle' },
          },
        })
        .then(onFinishedEditing)
    },
    [replicache]
  )

  return React.useCallback(
    ([colNumber, rowNumber]: Item): GridCell => {
      // leave it here so that it recomputes the useCallback.
      rerender

      const columns = resolveVisibleColumns({
        selectedView,
        listTemplateId: block.typeOptions.targetBlockId,
        treeManager: treeManagerRef.current,
      })
      const isTitle = columns[colNumber]?.id === '__title'

      const row = rows[rowNumber]
      if (!row) return DEFAULT_EMPTY_CELL

      if (isTitle) {
        const title = getTitleBasedOnSelectedVariants({
          block: row,
          variantsToFilterBy: selectedDashboardVariants,
          parent: block,
        }).title
        const blockSourceBlockId = idToSourceBlockId(row)

        return {
          kind: GridCellKind.Custom,
          data: {
            kind: 'block-cell',
            placeholder: 'Untitled',
            title,
            hidden: row.hidden,
            type: row.type,
            highlight: blockSourceBlockId === template?.id,
            onClick: ({ metaKey }: { metaKey: boolean }) => {
              if (metaKey) {
                return window.open(`${tabPrefix}${blockSourceBlockId}`)
              }
              addTab({
                key: `${tabPrefix}${blockSourceBlockId}`,
                partiallyOpen: true,
              })
            },
          },
          copyData: row?.title,
          allowOverlay: !readonly,
          readonly,
        }
      } else {
        let cell: Block
        let node: TreeNode<Block>
        let nodeFromTemplate: TreeNode<Block>

        if (!treeManagerRef.current || !template) {
          return DEFAULT_EMPTY_CELL
        }

        if (!selectedView) {
          if (colNumber === 0) {
            const title = getTitleBasedOnSelectedVariants({
              block: row,
              variantsToFilterBy: selectedDashboardVariants,
              parent: block,
            }).title
            const blockSourceBlockId = idToSourceBlockId(row)

            return {
              kind: GridCellKind.Custom,
              data: {
                kind: 'block-cell',
                placeholder: 'Untitled',
                hidden: row.hidden,
                title,
                type: row.type,
                highlight: blockSourceBlockId === template?.id,
                onClick: ({ metaKey }: { metaKey: boolean }) => {
                  if (metaKey) {
                    return window.open(`${tabPrefix}${blockSourceBlockId}`)
                  }
                  addTab({
                    key: `${tabPrefix}${blockSourceBlockId}`,
                    partiallyOpen: true,
                  })
                },
              },
              copyData: title,
              allowOverlay: !readonly,
              readonly,
            }
          }

          const colId_normie = treeManagerRef.current.getChildrenKeys(
            idToSourceBlockId(template)
          )[colNumber - 1]
          if (!colId_normie) return DEFAULT_EMPTY_CELL

          const colBlock = treeManagerRef.current.getNode(colId_normie)?.value
          if (!colBlock) return DEFAULT_EMPTY_CELL

          const colId = idToSourceBlockId(colBlock)
          const data = getBlockFromColumnId(colId, {
            row,
            treeManager: treeManagerRef.current,
          })

          if (!data.block || !data.node) return DEFAULT_EMPTY_CELL

          cell = data.block
          node = data.node
          nodeFromTemplate = data.nodeFromTemplate
        } else {
          const currentColumn = columns[colNumber]
          if (!currentColumn) return DEFAULT_EMPTY_CELL

          const data = getBlockFromColumnId(currentColumn.id, {
            row,
            treeManager: treeManagerRef.current,
          })

          if (!data.block || !data.node) return DEFAULT_EMPTY_CELL

          cell = data.block
          node = data.node
          nodeFromTemplate = data.nodeFromTemplate
        }

        const addBlockTab = ({ metaKey }: { metaKey: boolean }) => {
          let newTabKey = idToSourceBlockId(cell)
          switch (cell.type) {
            case 'document':
            case 'instance':
            case 'component':
            case 'list':
              break
            default: {
              // else open its parent, if it exists
              if (node && node.parentKey && treeManagerRef.current) {
                newTabKey =
                  idToSourceBlockId(node.parentKey, {
                    getNode: treeManagerRef.current.getNode,
                  }) +
                  '#' +
                  idToSourceBlockId(cell)
              }
              break
            }
          }

          if (metaKey) {
            return window.open(`${tabPrefix}${newTabKey}`)
          }
          addTab({
            key: `${tabPrefix}${newTabKey}`,
            partiallyOpen: true,
          })
        }

        const cellPath = treeManagerRef.current.getPath(node)

        switch (cell.type) {
          case 'text': {
            const value =
              getValueBasedOnSelectedVariants({
                block: cell,
                variantsToFilterBy: selectedDashboardVariants,
              }) ?? ''

            return {
              kind: GridCellKind.Text,
              data: value,
              allowOverlay: !readonly,
              displayData: value.slice(0, 80),
              readonly,
            }
          }
          case 'boolean': {
            const value =
              getValueBasedOnSelectedVariants({
                block: cell,
                variantsToFilterBy: selectedDashboardVariants,
              }) ?? false

            return {
              kind: GridCellKind.Custom,
              copyData: value.toString(),
              data: {
                value,
                kind: 'boolean-cell',
              },
              allowOverlay: true,
              readonly,
            }
          }
          case 'icon': {
            const value =
              getValueBasedOnSelectedVariants({
                block: cell,
                variantsToFilterBy: selectedDashboardVariants,
              }) ?? false
            const v = (value || cell.value) ?? ''

            return {
              kind: GridCellKind.Custom,
              copyData: v,
              data: {
                value: v,
                kind: 'svg-cell',
                onClick: addBlockTab,
              },
              allowOverlay: false,
              readonly,
            }
          }
          case 'number': {
            const value =
              getValueBasedOnSelectedVariants({
                block: cell,
                variantsToFilterBy: selectedDashboardVariants,
              }) ?? undefined

            return {
              kind: GridCellKind.Number,
              data: value,
              allowOverlay: !readonly,
              displayData: value?.toString() ?? '',
              readonly,
            }
          }
          case 'og-image': {
            return {
              kind: GridCellKind.Custom,
              data: {
                kind: 'block-cell',
                title: 'Edit OG image',
                type: cell.type,
                onClick: addBlockTab,
              },
              copyData: '',
              allowOverlay: false,
              readonly,
            }
          }
          case 'select': {
            invariant(
              isSelectBlock(nodeFromTemplate.value),
              `Block from template should be select block, found ${nodeFromTemplate.value.type} instead`
            )

            const value = getValueBasedOnSelectedVariants({
              block: cell,
              variantsToFilterBy: selectedDashboardVariants,
            })

            return {
              kind: GridCellKind.Custom,
              data: {
                allowMultipleValues:
                  nodeFromTemplate.value.constraints.multiple,
                allowCreation: row.id === block.typeOptions.targetBlockId,
                values: value,
                kind: 'select-cell',
                options: nodeFromTemplate.value.constraints.acceptedValues,
              },
              allowOverlay: !readonly,
              readonly,
              copyData: value.join(', '),
            }
          }
          case 'event': {
            invariant(
              nodeFromTemplate.value && nodeFromTemplate.value.type === 'event',
              'Block should be event block'
            )

            if (nodeFromTemplate.value.typeOptions.view === 'table') {
              return {
                kind: GridCellKind.Custom,
                data: {
                  kind: 'block-cell',
                  type: 'event',
                  title: `Open ${nodeFromTemplate.value.title}`,
                  onClick: addBlockTab,
                },
                copyData: '',
                allowOverlay: false,
                readonly: true,
              }
            }

            const { isLoading, data } = getEventData(
              cell,
              nodeFromTemplate.value
            )

            if (isLoading) {
              return {
                kind: GridCellKind.Text,
                data: 'Loading...',
                allowOverlay: false,
                displayData: 'Loading...',
                readonly: true,
              }
            }

            const values = data.values
              ? data.values.map(({ count }) => count)
              : []

            return {
              kind: GridCellKind.Custom,
              data: {
                values,
                displayValues: values,
                color: LIST_BLOCK_DARK_THEME.accentColor,
                muted: row.hidden,
                graphKind: 'line',
                displayValue: values,
                idleValue: data.idleValue,
                kind: 'sparkline-cell',
                hideAxis: true,
                yAxis: [0, highestEventValue.current],
              },
              allowOverlay: false,
              readonly: true,
              copyData: '',
            }
          }
          case 'code-snippet': {
            const value = getValueBasedOnSelectedVariants({
              block: cell,
              variantsToFilterBy: selectedDashboardVariants,
            })

            return {
              kind: GridCellKind.Text,
              data: value.code,
              allowOverlay: !readonly,
              displayData: value.code.slice(0, 80),
            }
          }
          case 'rich-text': {
            const md = getRichTextContent(cell, selectedDashboardVariants) ?? ''

            return {
              kind: GridCellKind.Custom,
              data: {
                kind: 'block-cell',
                title: md,
                onClick: addBlockTab,
              },
              copyData: md,
              allowOverlay: false,
              readonly,
            }
          }
          case 'color': {
            const value = getValueBasedOnSelectedVariants({
              block: cell,
              variantsToFilterBy: selectedDashboardVariants,
            })

            return {
              kind: GridCellKind.Custom,
              copyData: value,
              data: {
                value,
                displayValue: value,
                kind: 'color-picker-cell',
              },
              allowOverlay: !readonly,
              readonly,
            }
          }
          case 'list':
          case 'document':
          case 'instance':
          case 'component': {
            let title = ''

            switch (cell.type) {
              case 'document':
              case 'instance':
              case 'component': {
                title = nodeFromTemplate.value.title
                break
              }
              case 'list': {
                // const childrenLength = treeManagerRef.current.getChildrenKeys(
                //   idToSourceBlockId(cell)
                // ).length

                // title = `${childrenLength} ${
                //   childrenLength === 1 ? 'row' : 'rows'
                // }`
                title = `Edit collection`
                break
              }
            }

            return {
              kind: GridCellKind.Custom,
              data: {
                kind: 'block-cell',
                title,
                type: cell.type,
                onClick: addBlockTab,
              },
              copyData: cell.title,
              allowOverlay: false,
              readonly,
            }
          }
          case 'reference': {
            // if (!treeManagerRef.current) {
            const value = getValueBasedOnSelectedVariants({
              block: cell,
              variantsToFilterBy: selectedDashboardVariants,
            })
            return {
              kind: GridCellKind.Custom,
              data: {
                kind: 'block-cell',
                title: `${value.targetBlockIds.length} ${value.targetBlockIds.length === 1 ? 'reference' : 'references'}`,
                type: cell.type,
                onClick: addBlockTab,
              },
              copyData: '',
              allowOverlay: false,
              readonly,
            }
            // }

            // const value = getValueBasedOnSelectedVariants({
            //   block: cell,
            //   variantsToFilterBy: selectedDashboardVariants,
            // })

            // const staleOptions: SelectOption[] = []
            // const targets = value.targetBlockIds
            //   .map((target) => {
            //     const targetBlock =
            //       treeManagerRef.current?.getNode(target)?.value
            //     if (!targetBlock) return null
            //     staleOptions.push({
            //       value: idToSourceBlockId(targetBlock),
            //       label: targetBlock.title || 'Untitled',
            //       icon: targetBlock.type,
            //     })
            //     return idToSourceBlockId(targetBlock)
            //   })
            //   .filter(Boolean)
            // invariant(
            //   isReferenceBlock(nodeFromTemplate.value),
            //   'Block should be reference block'
            // )
            // const possibleReferenceNodesForThisCell =
            //   getPossibleReferences(nodeFromTemplate.value) ?? []
            // const possibleReferencesForThisCell =
            //   possibleReferenceNodesForThisCell
            //     .map(({ value }) => {
            //       if (value.hidden) return null

            //       return {
            //         value: idToSourceBlockId(value),
            //         label: value.title || 'Untitled',
            //         icon: value.type,
            //       }
            //     })
            //     .filter(Boolean)

            // invariant(
            //   isReferenceBlock(nodeFromTemplate.value),
            //   `Block from template should be reference block, found ${nodeFromTemplate.value.type} instead`
            // )

            // return {
            //   kind: GridCellKind.Custom,
            //   data: {
            //     allowCreation: false,
            //     values: targets,
            //     kind: 'select-cell',
            //     allowMultipleValues:
            //       nodeFromTemplate.value.constraints.multiple,
            //     block: cell,
            //     parent: node,
            //     path: cellPath,
            //     options: possibleReferencesForThisCell,
            //     staleOptions: staleOptions,
            //   },
            //   copyData: targets.join(', '),
            //   allowOverlay: !readonly,
            // }
          }
          case 'date': {
            const { typeOptions } = nodeFromTemplate.value as DateBlock
            invariant(cell.type === 'date', 'Block should be date block')
            const value = getValueBasedOnSelectedVariants({
              block: cell,
              variantsToFilterBy: selectedDashboardVariants,
            })
            const displayDate = value
              ? isoStringToDisplayDate(value, { useTime: typeOptions.useTime })
              : getDateBlockPlaceholder({ useTime: typeOptions.useTime })

            return {
              kind: GridCellKind.Custom,
              data: {
                date: value,
                max: '9999-12-31',
                displayDate,
                format: typeOptions.useTime ? 'datetime-local' : 'date',
                kind: 'date-picker-cell',
              },
              allowOverlay: !readonly,
              copyData: displayDate,
            }
          }
          case 'media': {
            const { constraints } = nodeFromTemplate.value as MediaBlock
            const value = getValueBasedOnSelectedVariants({
              block: cell,
              variantsToFilterBy: selectedDashboardVariants,
            })

            if (value.status === 'uploading') {
              return {
                kind: GridCellKind.Custom,
                data: { kind: 'spinner-cell', copy: 'Uploading...' },
                copyData: '',
                allowOverlay: false,
              }
            } else {
              const actions: Action[] = []
              const isUploaded = value.status === 'uploaded'

              if (!readonly) {
                actions.push({
                  name: 'replace',
                  width: 24,
                  height: 24,
                  sprite: isUploaded ? 'symbol' : 'upload',
                  icon: isUploaded ? (
                    <SymbolIcon width={14} height={14} />
                  ) : (
                    <UploadIcon width={14} height={14} />
                  ),
                  onClick: (input) => {
                    if (input) input.click()
                  },
                })

                if (isUploaded) {
                  actions.push({
                    name: 'remove',
                    width: 24,
                    height: 24,
                    sprite: 'trash',
                    icon: <TrashIcon width={14} height={14} />,
                    onClick: (onFinishedEditing) => {
                      onRemoveAssetBlock(cell, onFinishedEditing, cellPath)
                    },
                  })
                }
              }

              let copyData: string
              if (value.status !== 'uploaded') {
                copyData = ''
              } else if (value.type === 'image') {
                copyData = JSON.stringify({
                  url: value.url,
                  altText: value.altText,
                  fileName: value.fileName,
                  fileSize: value.fileSize,
                  width: value.width,
                  height: value.height,
                  mimeType: value.mimeType,
                  cloudflareImage: value.cloudflareImage,
                })
              } else if (value.type === 'file') {
                const fileValue = value as Extract<
                  MediaBlock['value'],
                  { type: 'file' }
                >
                copyData =
                  value.status === 'uploaded'
                    ? JSON.stringify({
                        url: fileValue.url,
                        fileName: fileValue.fileName,
                        fileSize: fileValue.fileSize,
                        mimeType: fileValue.mimeType,
                      })
                    : ''
              } else if (value.type === 'video') {
                const videoValue = value as Extract<
                  MediaBlock['value'],
                  { type: 'video' }
                >
                copyData = JSON.stringify({
                  url: videoValue.url,
                  fileName: videoValue.fileName,
                  fileSize: videoValue.fileSize,
                  width: videoValue.width,
                  height: videoValue.height,
                  mimeType: videoValue.mimeType,
                })
              } else {
                const audioDuration = getAudioContent(cell)
                copyData =
                  value.status === 'uploaded'
                    ? JSON.stringify({
                        url: value.url,
                        fileName: value.fileName,
                        fileSize: value.fileSize,
                        mimeType: value.mimeType,
                      })
                    : ''

                return {
                  kind: GridCellKind.Custom,
                  allowOverlay: !readonly,
                  copyData,
                  readonly,
                  activationBehaviorOverride: 'single-click',
                  data: {
                    kind: 'media-picker-cell',
                    actions,
                    value,
                    duration: audioDuration ?? '',
                    allowedMimeTypes: getAllowedMimeTypesArray(
                      constraints.allowedMimeTypes
                    ),
                  },
                }
              }

              return {
                kind: GridCellKind.Custom,
                allowOverlay: !readonly,
                copyData,
                readonly,
                activationBehaviorOverride: 'single-click',
                data: {
                  kind: 'media-picker-cell',
                  actions,
                  value,
                  allowedMimeTypes: getAllowedMimeTypesArray(
                    constraints.allowedMimeTypes
                  ),
                },
              }
            }
          }
          default: {
            return DEFAULT_EMPTY_CELL
          }
        }
      }
    },
    [
      selectedView,
      rows,
      selectedDashboardVariants,
      block,
      template,
      readonly,
      addTab,
      tabPrefix,
      treeManagerRef,
      getEventData,
      highestEventValue,
      getRichTextContent,
      // getPossibleReferences,
      onRemoveAssetBlock,
      getAudioContent,
      rerender,
    ]
  )
}

export function getAllowedMimeTypesArray(
  allowedMimeTypes: MediaBlock['constraints']['allowedMimeTypes']
): string[] {
  return Object.entries(allowedMimeTypes ?? {}).reduce<string[]>(
    (acc, [cat, mimeTypes]) => {
      if (!mimeTypes || (Array.isArray(mimeTypes) && mimeTypes.length === 0)) {
        return acc
      }
      if (Array.isArray(mimeTypes)) {
        acc.push(...mimeTypes)
      } else {
        // When mimeTypes is `true`, use default types for that category
        acc.push(...getDefaultMimeTypesByCategory(cat))
      }
      return acc
    },
    []
  )
}
